import { useState, useEffect, useRef } from 'react';
import { motion, useMotionValue, useSpring } from 'framer-motion';
import { Github, Linkedin, Mail, Download } from 'lucide-react';

// Typing animation component
function TypingText({ text }: { text: string }) {
  const [displayedText, setDisplayedText] = useState('');
  const gradientRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    let currentIndex = 0;
    const typingInterval = setInterval(() => {
      if (currentIndex < text.length) {
        setDisplayedText(text.slice(0, currentIndex + 1));
        currentIndex++;
      } else {
        clearInterval(typingInterval);
      }
    }, 100); // 100ms delay between each letter

    return () => clearInterval(typingInterval);
  }, [text]);

  // Randomize gradient animation duration between 40-60s
  useEffect(() => {
    if (gradientRef.current && typeof window !== 'undefined') {
      const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      if (!mediaQuery.matches) {
        const duration = 40 + Math.random() * 20; // 40-60s
        gradientRef.current.style.animationDuration = `${duration}s`;
      }
    }
  }, []);

  const startPrithvi = text.indexOf("Prithvi");
  const endPrithvi = startPrithvi + "Prithvi".length;
  const beforePrithvi = displayedText.slice(0, startPrithvi);
  const prithviText = displayedText.slice(startPrithvi, endPrithvi);
  const afterPrithvi = displayedText.slice(endPrithvi);

  // Trigger mount sheen after typing completes
  useEffect(() => {
    if (displayedText.length === text.length) {
      const timer = setTimeout(() => {
        const chip = document.querySelector('.hero-name-chip');
        if (chip) {
          chip.classList.add('mounted');
          setTimeout(() => chip.classList.remove('mounted'), 900);
        }
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [displayedText.length, text.length]);

  return (
    <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight">
      {/* Before Prithvi - regular text */}
      {beforePrithvi.split('').map((char, index) => (
        <motion.span
          key={index}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.1 }}
        >
          {char === ' ' ? '\u00A0' : char}
        </motion.span>
      ))}
      
      {/* Prithvi - inside glass chip with gradient */}
      {prithviText && (
        <motion.span
          key="prithvi-chip"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.1 }}
          className="hero-name-chip"
        >
          <span ref={gradientRef} className="hero-name-gradient">
            {prithviText.split('').map((char, index) => (
              <motion.span
                key={index}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.1 }}
              >
                {char}
              </motion.span>
            ))}
          </span>
        </motion.span>
      )}
      
      {/* After Prithvi - regular text */}
      {afterPrithvi.split('').map((char, index) => (
        <motion.span
          key={startPrithvi + "Prithvi".length + index}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.1 }}
        >
          {char === ' ' ? '\u00A0' : char}
        </motion.span>
      ))}
      
      {/* Typing cursor */}
      {displayedText.length < text.length && (
        <motion.span
          animate={{ opacity: [1, 0] }}
          transition={{ duration: 0.5, repeat: Infinity }}
          className="inline-block w-1 h-12 bg-neon-green ml-1"
        />
      )}
    </h1>
  );
}

// Particle Canvas Component
function ParticleCanvas() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameRef = useRef<number>();
  const particlesRef = useRef<Array<{
    x: number;
    y: number;
    vx: number;
    vy: number;
    radius: number;
    opacity: number;
  }>>([]);
  const [isPaused, setIsPaused] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    // Check for prefers-reduced-motion
    if (typeof window !== 'undefined') {
      const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      setPrefersReducedMotion(mediaQuery.matches);
      
      const handleChange = (e: MediaQueryListEvent) => {
        setPrefersReducedMotion(e.matches);
        if (e.matches) setIsPaused(true);
      };
      
      mediaQuery.addEventListener('change', handleChange);
      
      // Check if tab is hidden
      const handleVisibilityChange = () => {
        setIsPaused(document.hidden);
      };
      
      document.addEventListener('visibilitychange', handleVisibilityChange);
      
      return () => {
        mediaQuery.removeEventListener('change', handleChange);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
    }
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Initialize particles (40-80 random)
    const particleCount = Math.floor(Math.random() * 41) + 40; // 40-80
    particlesRef.current = Array.from({ length: particleCount }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      vx: (Math.random() - 0.5) * 0.5,
      vy: (Math.random() - 0.5) * 0.5,
      radius: Math.random() * 1.5 + 0.5,
      opacity: Math.random() * 0.5 + 0.2,
    }));

    const animate = () => {
      if (isPaused || prefersReducedMotion) {
        animationFrameRef.current = requestAnimationFrame(animate);
        return;
      }

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      particlesRef.current.forEach((particle) => {
        // Update position
        particle.x += particle.vx;
        particle.y += particle.vy;

        // Wrap at bounds
        if (particle.x < 0) particle.x = canvas.width;
        if (particle.x > canvas.width) particle.x = 0;
        if (particle.y < 0) particle.y = canvas.height;
        if (particle.y > canvas.height) particle.y = 0;

        // Draw particle
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.radius, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(0, 255, 65, ${particle.opacity})`;
        ctx.fill();
      });

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isPaused, prefersReducedMotion]);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full pointer-events-none"
      style={{ zIndex: 0 }}
    />
  );
}

export default function GlitchIntro() {
  const [isVisible, setIsVisible] = useState(false);
  const resumeButtonRef = useRef<HTMLAnchorElement>(null);
  const avatarRef = useRef<HTMLDivElement>(null);
  const socialIconsRef = useRef<HTMLDivElement>(null);
  
  // Motion values for pointer tracking
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const springX = useSpring(mouseX, { stiffness: 150, damping: 15 });
  const springY = useSpring(mouseY, { stiffness: 150, damping: 15 });

  useEffect(() => {
    // Show content after a brief delay for smooth entrance
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // Check for prefers-reduced-motion
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (mediaQuery.matches) {
      // Reset to center if reduced motion is enabled
      mouseX.set(0);
      mouseY.set(0);
      return;
    }

    let lastTime = 0;
    const throttleDelay = 100; // 60-120ms range, using 100ms

    const handleMouseMove = (e: MouseEvent) => {
      const now = Date.now();
      if (now - lastTime < throttleDelay) return;
      lastTime = now;

      const { clientX, clientY } = e;
      const centerX = window.innerWidth / 2;
      const centerY = window.innerHeight / 2;

      // Calculate offset from center (normalized to -1 to 1)
      const offsetX = (clientX - centerX) / centerX;
      const offsetY = (clientY - centerY) / centerY;

      // Update motion values (2-6px range)
      mouseX.set(offsetX * 6);
      mouseY.set(offsetY * 6);
    };

    window.addEventListener('mousemove', handleMouseMove);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, [mouseX, mouseY]);

  // Apply spring values to elements
  useEffect(() => {
    const updateTransforms = () => {
      const x = springX.get();
      const y = springY.get();
      
      if (resumeButtonRef.current) {
        resumeButtonRef.current.style.transform = `translate(${x}px, ${y}px)`;
      }
      if (avatarRef.current) {
        avatarRef.current.style.transform = `translate(${x * 0.8}px, ${y * 0.8}px)`;
      }
      if (socialIconsRef.current) {
        socialIconsRef.current.style.transform = `translate(${x * 0.6}px, ${y * 0.6}px)`;
      }
    };

    const unsubscribeX = springX.on('change', updateTransforms);
    const unsubscribeY = springY.on('change', updateTransforms);

    return () => {
      unsubscribeX();
      unsubscribeY();
    };
  }, [springX, springY]);

  return (
    <section 
      id="home" 
      className="relative min-h-screen flex items-center justify-center bg-black text-white overflow-hidden py-20 md:py-32"
    >
      {/* Particle Canvas Background */}
      <ParticleCanvas />
      
      {/* Gradient Mesh Background - Very Low Opacity */}
      <div className="absolute inset-0 z-0 opacity-[0.03]">
        <div
          className="w-full h-full"
          style={{
            backgroundImage: `
              radial-gradient(circle at 20% 30%, rgba(16, 185, 129, 0.4) 0%, transparent 50%),
              radial-gradient(circle at 80% 70%, rgba(6, 182, 212, 0.4) 0%, transparent 50%),
              radial-gradient(circle at 50% 50%, rgba(168, 85, 247, 0.4) 0%, transparent 50%)
            `,
            backgroundSize: '100% 100%',
            filter: 'blur(80px)',
          }}
        />
      </div>
      
      {/* Subtle background grid - maintaining dark theme */}
      <div className="absolute inset-0 opacity-5 z-0">
        <div
          className="w-full h-full"
          style={{
            backgroundImage: `
              linear-gradient(rgba(0, 255, 65, 0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(0, 255, 65, 0.1) 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px',
          }}
        />
      </div>

      {/* Main Content Container */}
      <div className="max-w-7xl mx-auto px-6 md:px-8 lg:px-12 relative z-10 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16 items-center">
          
          {/* Left Column - 60% (3/5 cols) */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 30 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="lg:col-span-3 space-y-8"
          >
            {/* H1 - Hi, I'm Prithvi with typing animation */}
            <TypingText text="Hi, I'm Prithvi" />

            {/* H2/Subtitle - Full-stack Developer & ML Enthusiast */}
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 20 }}
              transition={{ duration: 0.8, delay: 0.8 }}
              className="text-2xl md:text-3xl lg:text-4xl font-semibold text-gray-300"
            >
              Full-stack Developer & ML Enthusiast
            </motion.h2>

            {/* Bio */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 20 }}
              transition={{ duration: 0.8, delay: 1.0 }}
              className="text-lg md:text-xl text-gray-400 max-w-2xl leading-relaxed"
            >
              I create beautiful, functional, and user-centered digital experiences. With experience in web development and machine learning, I bring ideas to life through clean code and thoughtful design.
            </motion.p>

            {/* Button - View My Resume */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 20 }}
              transition={{ duration: 0.8, delay: 1.2 }}
              className="pt-4"
            >
              <motion.a
                ref={resumeButtonRef}
                href="https://drive.google.com/file/d/1GIklfem95lTJyyZPaIBLm-qQp0tMolGG/view?usp=drive_link"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05, boxShadow: '0 0 20px rgba(0, 255, 65, 0.5)' }}
                whileTap={{ scale: 0.95 }}
                className="inline-flex items-center space-x-2 px-8 py-4 bg-neon-green text-black rounded-lg font-bold transition-all duration-300 hover:bg-neon-green/90 focus-visible:outline focus-visible:outline-3 focus-visible:outline-neon-green focus-visible:outline-offset-2"
                style={{ willChange: 'transform' }}
              >
                <Download size={20} />
                <span>View My Resume</span>
              </motion.a>
            </motion.div>

            {/* Social Icons Row */}
            <motion.div
              ref={socialIconsRef}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 20 }}
              transition={{ duration: 0.8, delay: 1.4 }}
              className="flex flex-col space-y-3 pt-4"
              style={{ willChange: 'transform' }}
            >
              <span className="text-sm text-gray-400 font-mono">Follow me:</span>
              <div className="flex items-center space-x-4">
                <motion.a
                  href="https://github.com/16Prithvi"
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.1, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  className="p-3 bg-gray-800/50 rounded-lg border border-gray-700 hover:border-neon-green hover:bg-gray-800 transition-all duration-300 focus-visible:outline focus-visible:outline-3 focus-visible:outline-gray-900 focus-visible:outline-offset-2"
                  aria-label="GitHub"
                >
                  <Github size={24} className="text-gray-300 hover:text-neon-green transition-colors" />
                </motion.a>
                <motion.a
                  href="https://www.linkedin.com/in/prithvi-ganiger-10bb11365"
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.1, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  className="p-3 bg-gray-800/50 rounded-lg border border-gray-700 hover:border-neon-blue hover:bg-gray-800 transition-all duration-300 focus-visible:outline focus-visible:outline-3 focus-visible:outline-gray-900 focus-visible:outline-offset-2"
                  aria-label="LinkedIn"
                >
                  <Linkedin size={24} className="text-gray-300 hover:text-neon-blue transition-colors" />
                </motion.a>
                <motion.a
                  href="mailto:prithviganiger@gmail.com"
                  whileHover={{ scale: 1.1, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  className="p-3 bg-gray-800/50 rounded-lg border border-gray-700 hover:border-neon-pink hover:bg-gray-800 transition-all duration-300 focus-visible:outline focus-visible:outline-3 focus-visible:outline-gray-900 focus-visible:outline-offset-2"
                  aria-label="Email"
                >
                  <Mail size={24} className="text-gray-300 hover:text-neon-pink transition-colors" />
                </motion.a>
              </div>
            </motion.div>
          </motion.div>

          {/* Right Column - 40% (2/5 cols) - Avatar */}
          <motion.div
            ref={avatarRef}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: isVisible ? 1 : 0, scale: isVisible ? 1 : 0.8 }}
            transition={{ duration: 1, delay: 0.6 }}
            className="lg:col-span-2 flex items-center justify-center relative"
            style={{ willChange: 'transform' }}
          >
            {/* Avatar Container with Orbiters */}
            <div className="relative w-full max-w-md">
              {/* Orbiting Particles - 3 Orbiters */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                {[1, 2, 3].map((index) => {
                  const duration = 8 + index * 3; // 8, 11, 14s
                  const direction = index % 2 === 0 ? 1 : -1; // Alternate directions
                  const radius = 140 + index * 30; // Varying orbit radius (140, 170, 200px)
                  const dotSize = 4 + index * 0.5; // 4, 4.5, 5px
                  
                  return (
                    <motion.div
                      key={index}
                      className="absolute"
                      style={{
                        left: '50%',
                        top: '50%',
                        width: `${radius * 2}px`,
                        height: `${radius * 2}px`,
                        marginLeft: `-${radius}px`,
                        marginTop: `-${radius}px`,
                        willChange: 'transform',
                      }}
                      animate={{
                        rotate: [0, 360 * direction],
                      }}
                      transition={{
                        duration: duration,
                        repeat: Infinity,
                        ease: 'linear',
                      }}
                    >
                      <div
                        className="absolute rounded-full bg-white"
                        style={{
                          width: `${dotSize}px`,
                          height: `${dotSize}px`,
                          opacity: 0.35,
                          left: `${radius - dotSize / 2}px`,
                          top: `-${dotSize / 2}px`,
                        }}
                      />
                    </motion.div>
                  );
                })}
              </div>

              {/* Avatar Image */}
              <img
                src="/images/ppp.jpg"
                alt="Prithvi M Ganiger"
                className="relative z-10 w-full h-auto rounded-2xl border-2 border-neon-green/30 shadow-lg shadow-neon-green/20"
                style={{ objectPosition: '35% 25%' }}
              />
            </div>
          </motion.div>
        </div>
      </div>

      {/* Subtle scanline effect - maintaining dark theme aesthetic */}
      <div className="absolute inset-0 pointer-events-none opacity-5">
        <div className="scanlines" />
      </div>
    </section>
  );
}
